import { ITBox, QboAlert, QboButton, QboCard, QboTypography } from '@ui/Components';
import TableConfirmationBlock from '@ui/Blocks/Shared/TableConfirmationBlock';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const mailboxHeaders = [
  {
    id: 1,
    label: 'ID',
    columnName: 'id',
  },
  {
    id: 2,
    label: 'Email',
    columnName: 'email',
  },
  {
    id: 3,
    label: 'Organization Name',
    columnName: 'organizationName',
  },
  {
    id: 4,
    label: 'Partner Name',
    columnName: 'partnerName',
  },
];

const missingEmailHeaders = [
  {
    id: 1,
    label: 'Email',
    columnName: 'email',
  },
];

export default function MailboxDeactivationConfirmSection({
  isSubmitting,
  errorMessage,
  validateResult,
  formData,
  onSubmit,
  onBack,
}) {
  const { t } = useTranslation();

  const { validMailboxes, invalidMailboxes, missingMailboxes } = useMemo(() => {
    const valids = [];
    const invalids = [];
    const mailboxes = validateResult.map((m) => m.email.toLowerCase());
    const missing = formData.emailAccounts
      .split(',')
      .filter((ea) => !mailboxes.includes(ea))
      .map((m) => ({
        email: m,
      }));

    validateResult.forEach((m) => {
      if (m.ableToRunBackup) valids.push(m);
      else invalids.push(m);
    });

    return { validMailboxes: valids, invalidMailboxes: invalids, missingMailboxes: missing };
  }, [validateResult, formData]);

  return (
    <ITBox className="TransferSubscriptionFormPage__wrapper" direction="column">
      {errorMessage && (
        <QboAlert type="error" style={{ fontSize: '1.15em' }}>
          {errorMessage}
        </QboAlert>
      )}
      {!!validMailboxes.length && (
        <TableConfirmationBlock
          list={validMailboxes}
          headers={mailboxHeaders}
          title={`The deactivation process can now be initiated for ${validMailboxes.length} ${
            validMailboxes.length > 1 ? 'mailboxes' : 'mailbox'
          }`}
        />
      )}
      {!!invalidMailboxes.length > 0 && (
        <TableConfirmationBlock
          list={invalidMailboxes}
          headers={mailboxHeaders}
          title={`Deactivation cannot be initiated for ${invalidMailboxes.length} ${
            invalidMailboxes.length > 1 ? 'mailboxes' : 'mailbox'
          } because it is either inactive or has a backup in running status.`}
        />
      )}
      {!!missingMailboxes.length > 0 && (
        <TableConfirmationBlock
          list={missingMailboxes}
          headers={missingEmailHeaders}
          title={`${missingMailboxes.length} ${
            missingMailboxes.length > 1 ? 'mailboxes' : 'mailbox'
          } cannot be found in the organization.`}
        />
      )}
      <QboCard
        titleCard={t('mailbox_deactivation.modal.information')}
        className="TransferSubresellerPage__information-wrapper"
        isBordered>
        <ITBox className="TransferSubresellerPage__information-box" direction="column">
          <div style={{ display: 'flex', marginTop: 7 }}>
            <QboTypography sx={{ fontSize: 13, fontWeight: 700 }}>Support Ticket:</QboTypography>
            <QboTypography wrap sx={{ fontSize: 13 }}>
              &nbsp;
              {formData.supportTicket || '-'}
            </QboTypography>
          </div>
          <div style={{ display: 'flex', marginTop: 7 }}>
            <QboTypography sx={{ fontSize: 13, fontWeight: 700 }}>Escalation Ticket:</QboTypography>
            <QboTypography wrap sx={{ fontSize: 13 }}>
              &nbsp;
              {formData.escalationTicket || '-'}
            </QboTypography>
          </div>
          <div style={{ display: 'flex', marginTop: 7 }}>
            <QboTypography sx={{ fontSize: 13, fontWeight: 700 }}>Description:</QboTypography>
            <QboTypography wrap sx={{ fontSize: 13 }}>
              &nbsp;
              {formData.description || '-'}
            </QboTypography>
          </div>
        </ITBox>
      </QboCard>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {' '}
        <QboButton
          disabled={isSubmitting}
          sx={{ width: 'max-content' }}
          variant="outlined"
          color="secondary"
          onClick={() => onBack()}>
          {t('mailbox_deactivation.modal.back_button_text')}
        </QboButton>
        <QboButton
          disabled={isSubmitting || !validMailboxes.length}
          sx={{ width: 'max-content' }}
          variant="contained"
          onClick={onSubmit}>
          {t('mailbox_deactivation.modal.submit_button_text')}
        </QboButton>
      </div>
    </ITBox>
  );
}

MailboxDeactivationConfirmSection.propTypes = {
  isSubmitting: PropTypes.bool,
  errorMessage: PropTypes.string,
  validateResult: PropTypes.arrayOf(PropTypes.object),
  formData: PropTypes.object,
  onSubmit: PropTypes.func,
  onBack: PropTypes.func,
};

MailboxDeactivationConfirmSection.defaultProps = {
  isSubmitting: false,
  errorMessage: null,
  validateResult: [],
  formData: {},
  onSubmit: () => {},
  onBack: () => {},
};
